import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HistoryEntry, TagType, TagRequest, TagResponse } from '@/types/api';

// Get max history entries from environment or use default
const MAX_HISTORY_ENTRIES = parseInt(import.meta.env.VITE_HISTORY_MAX_ENTRIES || '50', 10);

interface HistoryState {
  executions: HistoryEntry[];
}

interface HistoryActions {
  addEntry: (entry: Omit<HistoryEntry, 'id' | 'createdAt'>) => void;
  removeEntry: (id: string) => void;
  clearHistory: () => void;
  getEntryById: (id: string) => HistoryEntry | undefined;
  getEntriesByType: (type: TagType) => HistoryEntry[];
}

type HistoryStore = HistoryState & HistoryActions;

/**
 * Generate a unique ID for history entries
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * History store - persisted to localStorage
 * Stores execution history for all tag extraction requests
 */
export const useHistoryStore = create<HistoryStore>()(
  persist(
    (set, get) => ({
      // Initial state
      executions: [],

      // Actions
      addEntry: (entry) => {
        const newEntry: HistoryEntry = {
          id: generateId(),
          createdAt: new Date().toISOString(),
          ...entry,
        };

        set((state) => ({
          executions: [newEntry, ...state.executions].slice(0, MAX_HISTORY_ENTRIES),
        }));
      },

      removeEntry: (id) => {
        set((state) => ({
          executions: state.executions.filter((e) => e.id !== id),
        }));
      },

      clearHistory: () => {
        set({ executions: [] });
      },

      getEntryById: (id) => {
        return get().executions.find((e) => e.id === id);
      },

      getEntriesByType: (type) => {
        return get().executions.filter((e) => e.type === type);
      },
    }),
    {
      name: 'history-storage',
      version: 1,
    }
  )
);

/**
 * Helper to create a history entry from API call results
 */
export function createHistoryEntry(
  type: TagType,
  requestPayload: TagRequest,
  responsePayload: TagResponse | null,
  responseHeaders: Record<string, string>,
  error: string | null
): Omit<HistoryEntry, 'id' | 'createdAt'> {
  return {
    type,
    requestPayload,
    responsePayload,
    responseHeaders,
    error,
  };
}
